import {
  type KeyboardEvent,
  type Ref,
  useId,
  useImperativeHandle,
  useRef,
} from 'react'
import { contentCopy } from '@/copy'
import { cx } from '../cx'
import { Input } from '../Field/Field'
import { Icon } from '../Icon/Icon'
import { Spinner } from '../Loading/Loading'
import styles from './SearchInput.module.css'

/**
 * A filter field: a leading magnifying glass, a clear button once there is a
 * value, and an optional result count announced as a status. `onValue` fires
 * per keystroke; callers debounce remote filters themselves.
 */
export function SearchInput({
  value,
  onValue,
  label,
  placeholder,
  resultCount,
  totalCount,
  countNoun,
  loading,
  className,
  ref,
}: {
  value: string
  onValue: (value: string) => void
  /** The accessible name. */
  label: string
  /** What is being filtered, e.g. "Filter threads". */
  placeholder?: string
  resultCount?: number
  totalCount?: number
  countNoun?: string
  /** Shows a spinner before the kept count while a remote filter runs. */
  loading?: boolean
  className?: string
  /** Reaches the input, for a focus shortcut. */
  ref?: Ref<HTMLInputElement>
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])
  const countId = useId()

  // A search field would clear itself on Escape; the text is kept instead.
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.currentTarget.blur()
    }
  }

  return (
    <div className={cx(styles.searchInput, className)}>
      <div className={styles.box}>
        <Icon
          name="magnifying-glass"
          size="icon-embedded"
          className={styles.glyph}
        />
        <Input
          ref={inputRef}
          type="search"
          className={styles.input}
          aria-label={label}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onValue(e.target.value)}
          onKeyDown={onKeyDown}
          aria-describedby={resultCount != null ? countId : undefined}
        />
        {value !== '' && (
          <span className={styles.tail}>
            <button
              type="button"
              className={styles.clear}
              aria-label={contentCopy.searchInput.clear}
              onClick={() => {
                onValue('')
                inputRef.current?.focus()
              }}
            >
              <Icon name="cross-2" size="icon-embedded" />
            </button>
          </span>
        )}
      </div>
      {/* Always mounted, so the first count is announced: only its text changes. */}
      <p id={countId} role="status" className={styles.count}>
        {resultCount != null && (
          <>
            {loading && <Spinner />}
            {contentCopy.searchInput.count(resultCount, totalCount, countNoun)}
          </>
        )}
      </p>
    </div>
  )
}
